import React from 'react'
import { twMerge } from 'tailwind-merge'
import classNames from 'classnames'
import { Tooltip } from './Tooltip'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { Icon } from '../Icon/Icon'

export type PillProps = {
  /** These colors modify the color and background of the pill */
  color?:
    | 'gray'
    | 'red'
    | 'green'
    | 'blue'
    | 'yellow'
    | 'purple'
    | 'indigo'
    | 'pink'
    | 'rose'
    | 'amber'
    | 'lime'
    | 'cyan'
    | 'emerald'
    | 'teal'
    | 'violet'
    | 'fuchsia'
    | 'orange'

  /** This will scan the pill to different sizes */
  size?: 'sm' | 'md' | 'lg' | 'xl'

  /** Icon That is displayed to the left of the content */
  icon?: IconDefinition

  /** If defined, this will show a tooltip when hovered. Normally a string. */
  tooltipText?: React.ReactNode

  /** Content that displays in the pill. Normally a string */
  children?: React.ReactNode

  /** Specific Pill Variant */
  variant?: 'square' | 'base'

  /** If this is true, it will condense multiple sibling pills on mobile */
  condenseOnMobile?: boolean

  /** These classes are applied to the element that surrounds the content and tooltip */
  containerClasses?: string

  /** These classes are only applied to the icon */
  iconClasses?: string

  /** These classes are only applied to the text that is displayed to the right of the icon */
  textClasses?: string

  /** These classes are only applied to the tooltip when it is in a loading state, the icon is visible but not the text. */
  loadingClasses?: string

  /** This adds contrast to the specific color */
  contrast?: boolean

  /** This will show pill with a default width */
  loading?: boolean
}

export const textBackgroundColorClasses = (color?: PillProps['color']) =>
  classNames({
    'bg-purple-200 text-purple-800': color === 'purple',
    'bg-blue-200 text-blue-800': color === 'blue',
    'bg-yellow-200 text-yellow-800': color === 'yellow',
    'bg-red-200 text-red-800': color === 'red',
    'bg-base-200 text-base-800': color === 'gray',
    'bg-green-200 text-green-800': color === 'green',
    'bg-indigo-200 text-indigo-800': color === 'indigo',
    'bg-pink-200 text-pink-800': color === 'pink',
    'bg-rose-200 text-rose-800': color === 'rose',
    'bg-amber-200 text-amber-800': color === 'amber',
    'bg-lime-200 text-lime-800': color === 'lime',
    'text-cyan-800 bg-cyan-100 dark:bg-cyan-800 dark:text-cyan-100':
      color === 'cyan',
    'bg-emerald-200 text-emerald-800': color === 'emerald',
    'bg-teal-200 text-teal-800': color === 'teal',
    'bg-violet-200 text-violet-800': color === 'violet',
    'bg-fuchsia-200 text-fuchsia-800': color === 'fuchsia',
    'bg-orange-200 text-orange-800': color === 'orange',
    'text-base-content-muted bg-base-200': !color,
  })

export const textBackgroundColorClassesContrasted = (
  color?: PillProps['color'],
) =>
  classNames({
    'bg-purple-100 text-purple-900': color === 'purple',
    'bg-blue-100 text-blue-900': color === 'blue',
    'bg-yellow-100 text-yellow-900': color === 'yellow',
    'bg-red-100 text-red-900': color === 'red',
    'bg-base-100 text-base-900': color === 'gray',
    'bg-green-100 text-green-900': color === 'green',
    'bg-indigo-100 text-indigo-900': color === 'indigo',
    'bg-pink-100 text-pink-900': color === 'pink',
    'bg-rose-100 text-rose-900': color === 'rose',
    'bg-amber-100 text-amber-900': color === 'amber',
    'bg-lime-100 text-lime-900': color === 'lime',
    'bg-cyan-100 text-cyan-900 dark:bg-cyan-800 dark:text-cyan-100':
      color === 'cyan',
    'bg-emerald-100 text-emerald-900': color === 'emerald',
    'bg-teal-100 text-teal-900': color === 'teal',
    'bg-violet-100 text-violet-900': color === 'violet',
    'bg-fuchsia-100 text-fuchsia-900': color === 'fuchsia',
    'bg-orange-100 text-orange-900': color === 'orange',
    'text-base-content-muted bg-base-100': !color,
  })

/** A Pill is meant to display 1-3 words */
export const Pill = (props: PillProps) => {
  const { size = 'md', variant = 'square' } = props

  const containerSizeClasses = classNames({
    'gap-[6px] text-xs px-2 h-[20px] font-bold': size === 'sm',
    'gap-[7px] py-1 text-xs font-bold h-[25px]': size === 'md',
    'px-[14px]': size === 'md' && variant === 'base',
    'px-[10px] py-[6px] h-[25px]': size === 'md' && variant === 'square',
    'gap-2 px-4 py-[6px] sm:py-2 text-sm min-h-[30px] max-h-fit': size === 'lg',
    'font-bold': size === 'lg' && variant === 'base' && !props.color,
    'font-medium':
      size === 'lg' &&
      (variant === 'square' || (variant === 'base' && !!props.color)),
    'gap-2 px-4 py-2 font-medium text-lg': size === 'xl',
    'ring-1 ring-inset': variant === 'square',
  })

  const containerVariantClasses = classNames('', {
    'rounded-full': !props.condenseOnMobile,
    'first:rounded-l-lg last:rounded-r-lg': props.condenseOnMobile,
    'rounded-md': variant === 'square',
    'grow sm:first:rounded-l-full sm:last:rounded-r-full sm:first:rounded-r-full sm:last:rounded-l-full sm:rounded-full sm:grow-0 border border-base-300':
      variant === 'base',
  })

  const colorClasses = props.contrast
    ? textBackgroundColorClassesContrasted(props.color)
    : textBackgroundColorClasses(props.color)

  const containerClasses = twMerge(
    'flex items-center justify-center min-w-fit max-w-fit grow-0',
    containerSizeClasses,
    containerVariantClasses,
    colorClasses,
    props.containerClasses,
  )

  const iconSizeClasses = classNames({
    'h-[10px] w-[10px]': size === 'sm',
  })

  const iconClasses = twMerge(
    'block',
    colorClasses,
    iconSizeClasses,
    props.tooltipText ? 'cursor-pointer' : 'cursor-auto',
    props.iconClasses,
  )

  const textClasses = twMerge(
    'leading-none',
    colorClasses,
    props.textClasses,
    props.size !== 'lg' && 'font-medium',
    props.contrast && 'font-medium',
  )

  if (
    (typeof props.children === 'string' && props.children.length === 0) ||
    !props.children
  )
    return null

  if (props.loading)
    return (
      <p
        className={twMerge(
          containerClasses,
          'animate-pulse bg-slate-200 text-transparent w-[100px]',
          props.loadingClasses,
        )}>
        <Icon
          icon={props.icon}
          className={twMerge(iconClasses, 'bg-transparent')}
        />
        <span className={twMerge(textClasses, 'bg-transparent')}>
          {props.children}
        </span>
      </p>
    )

  if (props.tooltipText)
    return (
      <Tooltip
        className={containerClasses}
        wrapChildrenWithTag='p'
        tooltipContent={props.tooltipText}>
        <Icon
          icon={props.icon}
          className={twMerge(iconClasses, 'bg-transparent')}
        />
        <span className={twMerge(textClasses, 'bg-transparent')}>
          {props.children}
        </span>
      </Tooltip>
    )

  return (
    <p className={containerClasses}>
      <Icon
        icon={props.icon}
        className={twMerge(iconClasses, 'bg-transparent')}
      />
      <span className={twMerge(textClasses, 'bg-transparent')}>
        {props.children}
      </span>
    </p>
  )
}
